import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Check, Truck, ShieldCheck, MessageCircle, ChevronRight, Loader2, Heart, Zap, BadgeCheck, Banknote, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import CheckoutModal from "@/components/CheckoutModal";
import ProductCard, { RatingStars } from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { formatAED } from "@/data/products";

const WA_BASE = "https://wa.me/971568498289?text=";

const REVIEW_NAMES = ["Ahmed R.", "Sara M.", "Mohammed K.", "Fatima A.", "Omar S.", "Layla H.", "Khalid B.", "Priya N.", "James W.", "Aisha T."];
const REVIEW_TEXTS = {
  en: [
    "Excellent quality, exactly as described. Delivery to Dubai was next day.",
    "Very happy with this purchase. Genuine product, well packed.",
    "Good value for the price. Would order from Cambroo again.",
    "Works perfectly. Customer support on WhatsApp was very responsive.",
    "Fast delivery and the product matches the description.",
  ],
  ar: [
    "جودة ممتازة، مطابق تماماً للوصف. التوصيل إلى دبي كان في اليوم التالي.",
    "سعيد جداً بهذه الشراء. منتج أصلي وتغليف ممتاز.",
    "قيمة جيدة مقابل السعر. سأطلب من كامبرو مرة أخرى.",
    "يعمل بشكل مثالي. دعم العملاء عبر واتساب سريع الاستجابة.",
    "توصيل سريع والمنتج مطابق للوصف.",
  ],
};

function reviewsFor(product, isAr) {
  const seed = product.id.split("").reduce((s, c) => s + c.charCodeAt(0), 0);
  const texts = isAr ? REVIEW_TEXTS.ar : REVIEW_TEXTS.en;
  const base = Math.round(product.rating || 4.5);
  return [0, 1, 2].map((k) => ({
    name: REVIEW_NAMES[(seed + k * 3) % REVIEW_NAMES.length],
    rating: Math.min(5, Math.max(3, base + (k === 2 ? -1 : 0))),
    text: texts[(seed + k * 2) % texts.length],
    date: new Date(Date.now() - ((seed + k * 13) % 55 + 3) * 86400000).toLocaleDateString(isAr ? "ar-AE" : "en-GB"),
  }));
}

export default function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { products, categories, addItem, setCartOpen, wishlist, toggleWishlist } = useCart();
  const { t, isAr } = useLanguage();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  const product = products.find((p) => p.id === productId);
  const cat = categories.find((c) => c.id === product?.category);
  const sub = cat?.subs?.find((s) => s.id === product?.subcategory);
  const wished = product ? wishlist.includes(product.id) : false;
  const reviews = useMemo(() => (product ? reviewsFor(product, isAr) : []), [product, isAr]);

  const gallery = useMemo(
    () => (product ? (product.images?.length ? product.images : [product.image]).filter(Boolean).slice(0, 10) : []),
    [product]
  );
  const [mainImg, setMainImg] = useState(null);

  const related = useMemo(() => {
    if (!product) return [];
    const sameSub = products.filter((p) => p.subcategory === product.subcategory && p.id !== product.id);
    const sameCat = products.filter(
      (p) => p.category === product.category && p.subcategory !== product.subcategory && p.id !== product.id
    );
    return [...sameSub, ...sameCat].slice(0, 12);
  }, [product, products]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setQty(1);
    setAdded(false);
    setMainImg(null);
  }, [productId]);

  useEffect(() => {
    if (product) document.title = `${product.name} | Cambroo General Trading LLC`;
    return () => { document.title = "Cambroo — Mobile Accessories in Dubai | Cambroo General Trading LLC"; };
  }, [product]);

  const onAdd = () => {
    addItem(product.id, qty);
    setAdded(true);
    toast.success(isAr ? `${product.name} — ${t("toast_added")}` : `${product.name} ${t("toast_added")}`, {
      action: { label: t("toast_view"), onClick: () => setCartOpen(true) },
    });
    setTimeout(() => setAdded(false), 1600);
  };

  return (
    <>
      <Nav />
      <main className="min-h-screen bg-page px-4 pb-10 pt-[132px] md:pt-[124px] lg:px-8" data-testid="pdp-page">
        <div className="mx-auto max-w-7xl">
          {!product ? (
            <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4">
              {products.length === 0 ? (
                <Loader2 className="h-8 w-8 animate-spin text-link" />
              ) : (
                <>
                  <p className="text-lg font-bold text-brandink" data-testid="pdp-not-found">{t("pdp_not_found")}</p>
                  <Link to="/" data-testid="pdp-back" className="text-sm font-semibold text-link hover:underline">
                    {t("pdp_back")}
                  </Link>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Breadcrumb */}
              <nav data-testid="pdp-breadcrumb" className="mb-3 flex flex-wrap items-center gap-1 text-xs text-muted">
                <Link to="/" data-testid="pdp-breadcrumb-home" className="transition-colors hover:text-link">{t("pdp_home")}</Link>
                {cat && (
                  <>
                    <ChevronRight className="h-3 w-3 rtl:-scale-x-100" />
                    <button
                      data-testid="pdp-breadcrumb-category"
                      onClick={() => navigate(`/category/${cat.id}`)}
                      className="transition-colors hover:text-link"
                    >
                      {isAr ? cat.name_ar : cat.name}
                    </button>
                  </>
                )}
                {sub && (
                  <>
                    <ChevronRight className="h-3 w-3 rtl:-scale-x-100" />
                    <button
                      data-testid="pdp-breadcrumb-subcategory"
                      onClick={() => navigate(`/category/${cat.id}?sub=${sub.id}`)}
                      className="transition-colors hover:text-link"
                    >
                      {isAr ? sub.name_ar : sub.name}
                    </button>
                  </>
                )}
                <ChevronRight className="h-3 w-3 rtl:-scale-x-100" />
                <span className="max-w-[280px] truncate font-medium text-brandink">{product.name}</span>
              </nav>

              {/* Main panel */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="grid grid-cols-1 gap-8 rounded-lg border border-line bg-white p-5 md:grid-cols-2 md:p-8"
              >
                <div>
                  <div className="relative flex h-80 items-center justify-center md:h-[420px]">
                    {product.badge && (
                      <span className="absolute start-0 top-0 rounded bg-brand px-2 py-0.5 text-[10px] font-bold text-brandink">
                        {(isAr && product.badge_ar) || product.badge}
                      </span>
                    )}
                    <img
                      src={mainImg || gallery[0]}
                      alt={product.name}
                      data-testid="pdp-image"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  {gallery.length > 1 && (
                    <div className="mt-4 flex flex-wrap gap-2" data-testid="pdp-gallery">
                      {gallery.map((u, i) => (
                        <button
                          key={i}
                          data-testid={`pdp-thumb-${i}`}
                          onClick={() => setMainImg(u)}
                          className={`h-16 w-16 overflow-hidden rounded-md border bg-white p-1 transition-colors duration-200 ${
                            (mainImg || gallery[0]) === u ? "border-brandink" : "border-line hover:border-brandink/40"
                          }`}
                          aria-label={`Photo ${i + 1}`}
                        >
                          <img src={u} alt="" className="h-full w-full object-contain" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex flex-col">
                  <span data-testid="pdp-brand" className="inline-flex w-fit items-center gap-1.5 rounded-md border border-line bg-page px-2 py-1 text-[10px] font-extrabold uppercase tracking-wider text-brandink">
                    <BadgeCheck className="h-3.5 w-3.5 text-link" />
                    {product.brand || product.name.split(" ")[0]}
                  </span>
                  <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
                    {cat && (isAr ? cat.name_ar : cat.name)}
                    {sub ? ` · ${isAr ? sub.name_ar : sub.name}` : ""}
                  </p>
                  <h1 className="mt-1 text-2xl font-extrabold leading-snug text-brandink" data-testid="pdp-name">
                    {product.name}
                  </h1>
                  <div className="mt-2 flex items-center gap-3">
                    <RatingStars rating={product.rating || 4.5} count={product.rating_count} size="h-4 w-4" />
                    {product.express && (
                      <span data-testid="pdp-express" className="inline-flex items-center gap-1 rounded bg-brand px-1.5 py-0.5 text-[10px] font-extrabold lowercase italic text-brandink">
                        <Zap className="h-3 w-3" fill="currentColor" />
                        {t("express")}
                      </span>
                    )}
                  </div>
                  <p className="mt-3 text-3xl font-extrabold text-brandink" data-testid="pdp-price">
                    {formatAED(product.price)}
                  </p>

                  <div className="mt-6 flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <button
                        data-testid="pdp-qty-minus"
                        onClick={() => setQty((q) => Math.max(1, q - 1))}
                        className="flex h-10 w-10 items-center justify-center rounded-md border border-line text-brandink transition-colors hover:bg-page"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span data-testid="pdp-qty" className="w-8 text-center text-base font-bold">{qty}</span>
                      <button
                        data-testid="pdp-qty-plus"
                        onClick={() => setQty((q) => Math.min(99, q + 1))}
                        className="flex h-10 w-10 items-center justify-center rounded-md border border-line text-brandink transition-colors hover:bg-page"
                        aria-label="Increase quantity"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    <button
                      data-testid="pdp-add-button"
                      onClick={onAdd}
                      className={`flex flex-1 items-center justify-center gap-2 rounded-md py-3 text-sm font-bold transition-colors duration-200 ${
                        added ? "bg-success text-white" : "bg-brand text-brandink hover:bg-branddark"
                      }`}
                    >
                      {added ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                      {added ? t("added") : t("add_to_cart")}
                    </button>
                    <button
                      data-testid="pdp-wishlist-button"
                      onClick={() => toggleWishlist(product.id)}
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md border transition-colors ${
                        wished ? "border-destructive bg-red-50 text-destructive" : "border-line text-muted hover:text-destructive"
                      }`}
                      aria-label={t("nav_wishlist")}
                    >
                      <Heart className="h-5 w-5" fill={wished ? "currentColor" : "none"} />
                    </button>
                  </div>

                  <div className="mt-5 space-y-2 rounded-lg bg-page p-4 text-xs text-brandink/90">
                    <p className="flex items-center gap-2">
                      <Truck className="h-4 w-4 shrink-0 text-link" />
                      {t("pdp_delivery")}
                    </p>
                    <p className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 shrink-0 text-link" />
                      {t("feat_pay_s")}
                    </p>
                  </div>
                  <a
                    href={WA_BASE + encodeURIComponent(`Hello Cambroo! I'd like to ask about: ${product.name} (AED ${product.price})`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="pdp-whatsapp-ask"
                    className="mt-3 flex items-center justify-center gap-2 rounded-md border border-line py-2.5 text-xs font-bold text-brandink transition-colors hover:border-[#25D366] hover:text-[#25D366]"
                  >
                    <MessageCircle className="h-4 w-4" />
                    {t("pdp_ask")}
                  </a>

                  <div className="mt-5 flex items-start justify-between gap-2 border-t border-line pt-4" data-testid="pdp-trust-badges">
                    {[
                      { Icon: Truck, label: t("badge_delivery") },
                      { Icon: Banknote, label: t("badge_cod") },
                      { Icon: ShieldCheck, label: t("badge_warranty") },
                      { Icon: RotateCcw, label: t("badge_returns") },
                    ].map(({ Icon, label }, i) => (
                      <div key={i} className="flex flex-1 flex-col items-center gap-1.5 text-center" data-testid={`pdp-trust-${i}`}>
                        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand text-brandink">
                          <Icon className="h-5 w-5" />
                        </span>
                        <span className="text-[9px] font-bold leading-tight text-brandink/80">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Specifications */}
              {product.specs?.length > 0 && (
                <div className="mt-4 rounded-lg border border-line bg-white p-5 md:p-8" data-testid="pdp-specs">
                  <h2 className="text-base font-bold text-brandink">{t("pdp_specs")}</h2>
                  <dl className="mt-4 grid grid-cols-1 gap-x-10 gap-y-3 md:grid-cols-2">
                    {product.specs.map((s, i) => (
                      <div key={i} className="flex items-start gap-2 border-b border-line pb-3 text-sm" data-testid={`pdp-spec-${i}`}>
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                        <span className="text-brandink/90">{isAr ? s.ar : s.en}</span>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              {/* Description */}
              {((isAr && product.desc_ar) || product.desc) && (
                <div className="mt-4 rounded-lg border border-line bg-white p-5 md:p-8" data-testid="pdp-description">
                  <h2 className="text-base font-bold text-brandink">{t("pdp_description")}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-muted" data-testid="pdp-desc">
                    {(isAr && product.desc_ar) || product.desc}
                  </p>
                </div>
              )}

              {/* Customer reviews */}
              <div className="mt-4 rounded-lg border border-line bg-white p-5 md:p-8" data-testid="pdp-reviews">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-bold text-brandink">{t("reviews_title")}</h2>
                  <RatingStars rating={product.rating || 4.5} count={product.rating_count} size="h-4 w-4" />
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                  {reviews.map((r, i) => (
                    <div key={i} data-testid={`pdp-review-${i}`} className="rounded-lg border border-line bg-page p-4">
                      <RatingStars rating={r.rating} size="h-3 w-3" />
                      <p className="mt-2 text-sm leading-relaxed text-brandink/90">{r.text}</p>
                      <div className="mt-3 flex items-center justify-between">
                        <p className="text-xs font-bold text-brandink">{r.name}</p>
                        <p className="text-[10px] text-muted">{r.date}</p>
                      </div>
                      <p className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-success">
                        <ShieldCheck className="h-3 w-3" /> {t("verified_buyer")}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Related products */}
              {related.length > 0 && (
                <div className="mt-8" data-testid="pdp-related">
                  <h2 className="text-lg font-bold text-brandink">{t("pdp_related")}</h2>
                  <div className="no-scrollbar mt-4 flex gap-3 overflow-x-auto pb-2">
                    {related.map((p) => (
                      <div key={p.id} className="w-44 shrink-0 sm:w-52">
                        <ProductCard product={p} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
      <CartDrawer />
      <CheckoutModal />
    </>
  );
}
